import { FileCheck, AlertTriangle, Infinity, Clock } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const formatDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const calculateStreamPercent = (current, cap) => {
  if (!cap) return 0;
  return Math.min((current / cap) * 100, 100);
};

export const LicenseTracker = ({ licenses, tracks }) => {
  const getTrackTitle = (trackId) => {
    const track = tracks.find((t) => t.id === trackId);
    return track?.title || "Unknown Track";
  };

  const isApproachingCap = (license) => {
    if (license.license_type !== "limited" || !license.stream_cap) return false;
    const percent = calculateStreamPercent(license.current_streams, license.stream_cap);
    return percent >= 80;
  };

  const sortedLicenses = [...licenses].sort((a, b) => {
    // Put limited licenses approaching cap first
    const aWarning = isApproachingCap(a) ? 0 : 1;
    const bWarning = isApproachingCap(b) ? 0 : 1;
    return aWarning - bWarning;
  });

  return (
    <div
      className="bg-meta-surface border border-meta-border"
      data-testid="license-tracker"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-meta-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileCheck className="text-meta-neon" size={20} />
          <div>
            <h2 className="font-mono text-xs uppercase tracking-wider text-meta-text-muted">
              License Tracker
            </h2>
            <p className="font-sans font-semibold text-meta-text">Beat Leases & Licenses</p>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <Infinity size={14} className="text-meta-neon" />
            <span className="text-meta-text-muted">Unlimited</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-meta-warning" />
            <span className="text-meta-text-muted">Limited</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-meta-border hover:bg-transparent">
              <TableHead className="text-meta-text-muted font-mono text-xs uppercase">
                Beat Title
              </TableHead>
              <TableHead className="text-meta-text-muted font-mono text-xs uppercase">
                Track
              </TableHead>
              <TableHead className="text-meta-text-muted font-mono text-xs uppercase">
                Producer
              </TableHead>
              <TableHead className="text-meta-text-muted font-mono text-xs uppercase">
                License Type
              </TableHead>
              <TableHead className="text-meta-text-muted font-mono text-xs uppercase">
                Stream Cap / Usage
              </TableHead>
              <TableHead className="text-meta-text-muted font-mono text-xs uppercase">
                Purchase Date
              </TableHead>
              <TableHead className="text-meta-text-muted font-mono text-xs uppercase">
                Status
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedLicenses.map((license) => {
              const isWarning = isApproachingCap(license);
              const streamPercent = calculateStreamPercent(
                license.current_streams,
                license.stream_cap
              );

              return (
                <TableRow
                  key={license.id}
                  className={`border-meta-border hover:bg-meta-elevated transition-colors ${
                    isWarning ? "license-warning" : ""
                  }`}
                  data-testid={`license-row-${license.id}`}
                >
                  <TableCell className="font-mono text-meta-text">
                    {license.beat_title}
                  </TableCell>
                  <TableCell className="text-meta-text-secondary">
                    {getTrackTitle(license.track_id)}
                  </TableCell>
                  <TableCell className="text-meta-text-secondary">
                    {license.producer}
                  </TableCell>
                  <TableCell>
                    {license.license_type === "unlimited" ? (
                      <Badge className="bg-meta-neon-dim text-meta-neon border-none font-mono">
                        <Infinity size={12} className="mr-1" />
                        Unlimited
                      </Badge>
                    ) : (
                      <Badge className="bg-yellow-900/30 text-meta-warning border-none font-mono">
                        <Clock size={12} className="mr-1" />
                        Limited
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {license.license_type === "limited" && license.stream_cap ? (
                      <div className="space-y-1 min-w-[150px]">
                        <div className="flex justify-between text-xs font-mono">
                          <span className="text-meta-text-secondary">
                            {license.current_streams.toLocaleString()}
                          </span>
                          <span className="text-meta-text-muted">
                            / {license.stream_cap.toLocaleString()}
                          </span>
                        </div>
                        <div className="h-1.5 w-full bg-meta-base rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              streamPercent >= 90
                                ? "bg-meta-danger"
                                : streamPercent >= 80
                                ? "bg-meta-warning"
                                : "bg-meta-neon"
                            }`}
                            style={{ width: `${streamPercent}%` }}
                          />
                        </div>
                        {isWarning && (
                          <div className="flex items-center gap-1 text-meta-warning">
                            <AlertTriangle size={12} />
                            <span className="text-xs font-mono">
                              {streamPercent >= 90 ? "Critical!" : "Approaching cap"}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-meta-neon font-mono text-sm">∞</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-sm text-meta-text-secondary">
                    {formatDate(license.purchase_date)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={`font-mono ${
                        license.status === "active"
                          ? "bg-meta-neon-dim text-meta-neon"
                          : "bg-red-900/30 text-meta-danger"
                      } border-none`}
                    >
                      {license.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {licenses.length === 0 && (
          <div className="text-center py-12">
            <FileCheck className="mx-auto mb-3 text-meta-text-muted" size={32} />
            <p className="text-meta-text-muted">No licenses tracked yet</p>
          </div>
        )}
      </div>
    </div>
  );
};
